// pages/master-data/area/MasterAreaPage.jsx
'use client';
import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import { useAreaList, deleteArea } from '../../../stores/areaStore';
import { useCompanyList } from '../../../stores/companyStore';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import MasterAreaAddEdit from './MasterAreaAddEdit';
import { normalizeSearchFieldsConfig } from '../../../utils/searchHelper';
import { fleetApi } from '../../../api/axiosClient';
import { useLanguage } from '../../../utils/i18n';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

function MasterAreaPageContent() {
  const { t } = useLanguage();
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [searchField, setSearchField] = useState('name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [, setSearchExtraColumns] = useState([]);

  const [localArea, setLocalArea] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const notify = useNotifier();
  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'name', label: t('workingArea.name', 'Name') },
      { value: 'country', label: t('workingArea.country', 'Country'), tableName: 'm_country', fieldName: 'name', joinKey: 'country' },
      { value: 'city', label: t('workingArea.city', 'City') }
    ],
    [t]
  );

  const joinDefs = useMemo(
    () => ({
      company: { name: 'm_company', column_results: ['name'] },
      country: { name: 'm_country', column_results: ['name'] }
    }),
    []
  );

  const { companyOptions } = useCompanyList();

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c), // <-- always string
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const companyFilterColumns = useMemo(() => {
    let storedCompanyId = null;
    try {
      if (typeof window !== 'undefined') storedCompanyId = localStorage.getItem('companyId');
    } catch {
      storedCompanyId = null;
    }

    if (selectedCompanies && selectedCompanies.length > 0) {
      const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
      if (hasAll) return [];
      const values = selectedCompanies.map((s) => String(s.value));
      if (values.length === 1) {
        return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] }];
      } else {
        return [{ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') }];
      }
    }

    if (storedCompanyId && String(storedCompanyId) !== 'all') {
      return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: String(storedCompanyId) }];
    }
    return [];
  }, [selectedCompanies]);

  const searchFieldsConfig = useMemo(() => normalizeSearchFieldsConfig(searchFieldOptions), [searchFieldOptions]);

  const filterCompanyIds = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchValue = searchField === 'all' ? '' : searchRaw;

  const {
    data: areaFromHook,
    isLoading: hookLoading,
    totalRecord: hookTotal
  } = useAreaList(paginationModel.page, paginationModel.pageSize, hookSearchField, hookSearchValue, filterCompanyIds);

  const rows = searchField === 'all' ? localArea : areaFromHook || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecords = (searchField === 'all' ? localTotal : hookTotal) || 0;

  useEffect(() => {
    const t = setTimeout(() => {
      const v = String(searchRaw ?? '').trim();

      if (!v) {
        setSearchExtraColumns([...companyFilterColumns]);
        setSearchValue('');
        setPaginationModel((p) => ({ ...p, page: 0 }));
        return;
      }

      if (searchField === 'all') {
        setSearchExtraColumns([...companyFilterColumns]);
        setSearchValue(v);
        setPaginationModel((p) => ({ ...p, page: 0 }));
        return;
      }

      const searchCol = { name: searchField, logic_operator: 'like', operator: 'AND', value: v };
      setSearchExtraColumns([...companyFilterColumns, searchCol]);
      setSearchValue(v);
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(t);
  }, [searchRaw, searchField, companyFilterColumns]);

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();
    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;
        // 1. Rakit Filter Perusahaan (columns)
        const columns = [];
        if (selectedCompanies && selectedCompanies.length > 0) {
          const values = selectedCompanies.map((s) => String(s.value));
          if (values.length === 1) {
            columns.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] });
          } else {
            columns.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') });
          }
        }

        // Default minimal object untuk menghindari error backend jika columns kosong
        if (columns.length === 0) {
          columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
        }

        // 2. Rakit Array 'filters' dinamis untuk pencarian global
        let filters = [];
        if (searchValue) {
          filters = [
            { name: 'name', value: searchValue },
            { name: 'city', value: searchValue },
            { name: 'name', table_name: 'm_company', value: searchValue },
            { name: 'name', table_name: 'm_country', value: searchValue }
          ];
        }

        // 3. Susun Payload Utuh persis seperti MasterVehiclePage
        const payload = {
          paging: { start, length },
          orders: { columns: ['name'], ascending: true },
          joins: [
            { name: 'company', column_results: ['name'] },
            { name: 'country', column_results: ['name'] }
          ],
          columns: columns,
          filters: filters
        };
        const resp = await fleetApi.post('/area/search' + getTzParam(), payload, { signal: controller.signal });
        const data = resp?.data ?? null;
        const rows = Array.isArray(data) ? data : (data?.data ?? []);
        const total = Array.isArray(data) ? rows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

        setLocalArea(rows);
        setLocalTotal(total);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('country search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchField, searchValue, paginationModel, companyFilterColumns, searchFieldsConfig, joinDefs]);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      const columns = [];
      if (selectedCompanies && selectedCompanies.length > 0) {
        const values = selectedCompanies.map((s) => String(s.value));
        if (values.length === 1) {
          columns.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] });
        } else {
          columns.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') });
        }
      }

      if (columns.length === 0) {
        columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
      }

      let filters = [];
      if (searchValue) {
        if (searchField === 'all') {
          filters = [
            { name: 'name', value: searchValue },
            { name: 'city', value: searchValue },
            { name: 'name', table_name: 'm_company', value: searchValue },
            { name: 'name', table_name: 'm_country', value: searchValue }
          ];
        } else {
          const opt = searchFieldOptions.find((o) => o.value === searchField);
          if (opt) {
            const searchCol = {
              name: opt.fieldName || searchField,
              logic_operator: 'like',
              operator: 'AND',
              value: searchValue
            };
            if (opt.tableName) searchCol.table_name = opt.tableName;
            columns.push(searchCol);
          }
        }
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        joins: [
          { name: 'company', column_results: ['name'] },
          { name: 'country', column_results: ['name'] }
        ],
        columns: columns,
        filters: searchField === 'all' ? filters : undefined
      };

      const resp = await fleetApi.post('/area/search' + getTzParam(), payload);
      const data = resp?.data ?? null;
      return Array.isArray(data) ? data : (data?.data ?? []);
    } catch (err) {
      console.error('area export all error', err);
      return [];
    }
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch {
      console.log('eror');
    }
  };

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteArea(confirmTargetId);
      notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('notify.deleteFailed', 'Failed to delete'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  // search controls used by DataTable
  const handleSearch = ({ field, value }) => {
    setSearchField(field);
    setSearchRaw(value ?? '');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const columns = [
    {
      field: 'company',
      headerName: t('general.company', 'Company'),
      flex: 1.2,
      valueGetter: (params, row) => row?.join?.company_name || '-'
    },
    { field: 'name', headerName: t('workingArea.name', 'Name'), flex: 1 },
    {
      field: 'country',
      headerName: t('workingArea.country', 'Country'),
      flex: 1,
      valueGetter: (params, row) => row?.join?.country_name || '-'
    },
    { field: 'city', headerName: t('workingArea.city', 'City'), flex: 1 },

    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('masterCompany.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterCompany.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterCompany.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterCompany.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 100,
      headerAlign: 'center',
      renderCell: (params) => (
        <ActionMenu mode="inline" row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.name)} />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('workingArea.master', 'Master Area')}
        rows={rows}
        columns={columns}
        rowCount={totalRecords}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setSearchExtraColumns([...companyFilterColumns]);
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('general.searchByName', 'Search by Name')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        onFetchAllForExport={handleFetchAllForExport}
      />

      <MasterAreaAddEdit
        open={openDialog}
        id={editingId}
        onClose={handleDialogClose}
        onSuccess={(msg) => {
          setPaginationModel((p) => ({ ...p }));
        }}
        onError={(msg) => {
          notify({ message: msg || t('notify.failedSave', 'Operation failed'), severity: 'error' });
        }}
      />

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />
    </div>
  );
}

export default function MasterAreaPage() {
  return (
    <>
      <MasterAreaPageContent />
    </>
  );
}
